/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  composeReducers,
  formStateSnapshot,
  getFormStateSnapshotValue,
  markFormStateAsTouched,
  onChangeReducer,
  setDirty,
  setEntityField,
  setEntityFields,
  setFormStateSnapshotValue,
  setNestedEntityField,
  setPristine,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { DirtyType, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { arrayIterator } from '@gh/core-util';
import { first } from 'lodash';
import {
  getLeadSalesFolderId,
  LeadDetailsInit,
  LeadDetailsSaveResponse,
  LeadSalesFolder,
  LeadSalesFolderComplete,
  mergeSalesFolder,
  newLeadSalesFolder,
} from '../../resources/lead.model';
import {
  CurrentSalesFolder,
  LeadDetailsSelection,
  LeadDetailsState,
  LeadDetailsStateEvent,
  LeadDetailsStateEventType,
  LeadDetailsTabName,
  queryLeadCurrentSalesFolderHasErrors,
  queryLeadMasterInfoHasErrors,
  sfTable,
} from './lead-details.state';

const mainReducer = onChangeReducer(
  composeReducers(masterInfoReducer, salesFoldersReducer),
  toOnChangeReducer(globalFlagsOnChangeReducer),
);

const setSalesFolderDirty = (state: LeadDetailsState) =>
  setNestedEntityField(<any>state, ['currentSalesFolder', 'isDirty'], true);

export function leadDetailsReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.Load:
      return loadDetails(state, event.details, event.mode);
    case LeadDetailsStateEventType.TabSelected:
      return setEntityField(state, 'selection', leadDetailsSelection(state.selection, event));
    case LeadDetailsStateEventType.SetMasterInfo:
    case LeadDetailsStateEventType.SalesFolderAdd:
    case LeadDetailsStateEventType.SalesFolderEdit:
    case LeadDetailsStateEventType.SalesFolderReset:
    case LeadDetailsStateEventType.SalesFolderChange:
    case LeadDetailsStateEventType.SalesFolderLoad:
    case LeadDetailsStateEventType.CurrentSalesFolderSetMemo:
    case LeadDetailsStateEventType.CurrentSalesFolderSetGeneral:
      return mainReducer(state, event);
    case LeadDetailsStateEventType.CommitChanges:
      return commitChanges(state, event.saveResponse);
    case LeadDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case LeadDetailsStateEventType.ResetDirty:
      if (state.currentSalesFolder) {
        return setPristine(setEntityField(state, 'currentSalesFolder', setPristine(state.currentSalesFolder)));
      } else {
        return setPristine(state);
      }
    case LeadDetailsStateEventType.ChangeCommunicationListFilter:
      return changeCommunicationsFilterReducer(state, event);
    case LeadDetailsStateEventType.CommunicationListItemAdd:
    case LeadDetailsStateEventType.CommunicationListItemUpdate:
      return communicationListItemReducer(state, event);
    case LeadDetailsStateEventType.ContactSelect:
      return masterInfoReducer(state, event);
    default:
      return state;
  }
}

function communicationListItemReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.CommunicationListItemAdd:
      return setEntityField(
        state,
        'communications',
        [event.communicationListItem].concat(state.communications));
    case LeadDetailsStateEventType.CommunicationListItemUpdate:
      return setEntityField(
        state,
        'communications',
        () => state.communications.map((el) => {
            if (el.communicationId === event.communicationListItem.communicationId) {
              el = event.communicationListItem;
            }
            return el;
          }));
    default:
      return state;
  }
}

function masterInfoReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.SetMasterInfo: {
      const { customer } = getFormStateSnapshotValue(event.masterInfo);
      return setEntityFields(state, {
        'masterInfo': event.masterInfo,
        'email': customer && customer.email,
      });
    }
    case LeadDetailsStateEventType.ContactSelect:
      return setEntityFields(state, {
        'masterInfo': setFormStateSnapshotValue(
          state.masterInfo,
          (value) => setEntityField(value, 'customer', event.contact)),
        'version': event.contact && event.contact.version,
        'email': event.contact && event.contact.email,
        'isDirty': true,
      });
    default:
      return state;
  }
}

function changeCommunicationsFilterReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.ChangeCommunicationListFilter:
      return setEntityField(state, 'communicationListFilters', event.communicationListFilters);
    default:
      return state;
  }
}

function salesFoldersReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.SalesFolderAdd:
      return setSalesFolderDirty(addAndChooseNewSalesFolder(state));
    case LeadDetailsStateEventType.SalesFolderEdit:
      return setEntityField(state, 'currentSalesFolderInEditing', true);
    case LeadDetailsStateEventType.SalesFolderChange:
      // tslint:disable-next-line:no-non-null-assertion
      return chooseSalesFolder(state, getSalesFolderById(state.salesFolders, event.folderId)!);
    case LeadDetailsStateEventType.SalesFolderReset:
      if (!state.currentSalesFolder) {
        return state;
      }

      const sfToReset = state.currentSalesFolder;

      if (sfToReset.dirtyType === DirtyType.New) {
        // new entity was added into sales folders table and we have to remove it on reset
        const nextSfTable = sfTable.remove(state.salesFolders, sfToReset.id);

        return chooseSalesFolder(
          setEntityFields(state, {
            salesFolders: nextSfTable,
            currentSalesFolderInEditing: false,
          }),
          getFirstSalesFolder(nextSfTable)!); // tslint:disable-line:no-non-null-assertion
      } else {
        return chooseSalesFolder(
          setEntityField(state, 'currentSalesFolderInEditing', false),
          // tslint:disable-next-line:no-non-null-assertion
          getSalesFolderById(state.salesFolders, sfToReset.id)!);
      }
    case LeadDetailsStateEventType.SalesFolderLoad:
      return loadSalesFolder(state, event.salesFolder);
    case LeadDetailsStateEventType.CurrentSalesFolderSetMemo:
    case LeadDetailsStateEventType.CurrentSalesFolderSetGeneral:
      return syncCurrentSalesFolder(
        setEntityField(
          state,
          'currentSalesFolder',
          // tslint:disable-next-line:no-non-null-assertion
          currentSalesFolderReducer(state.currentSalesFolder!, event)));
    default:
      return state;
  }
}

/**
 * Guarantees that list of sales folders is synced with the latest changes of current sales folder.
 * It is necessary just for meetings (which has own separate save flow)
 */
function syncCurrentSalesFolder(state: LeadDetailsState): LeadDetailsState {
  const sfId = state.currentSalesFolder!.id; // tslint:disable-line:no-non-null-assertion

  const newSalesFolders = sfTable.mergeById(
    state.salesFolders,
    sfId,
    (sf) => sf);

  return setEntityField(state, 'salesFolders', newSalesFolders);
}

function currentSalesFolderReducer(state: CurrentSalesFolder, event: LeadDetailsStateEvent): CurrentSalesFolder {
  switch (event.type) {
    case LeadDetailsStateEventType.CurrentSalesFolderSetGeneral:
      return setEntityField(state, 'general', event.snapshot);
    case LeadDetailsStateEventType.CurrentSalesFolderSetMemo:
      return setEntityField(state, 'memo', event.memo);
    default:
      return state;
  }
}

function globalFlagsOnChangeReducer(state: LeadDetailsState, event: LeadDetailsStateEvent): LeadDetailsState {
  switch (event.type) {
    case LeadDetailsStateEventType.SetMasterInfo:
      return setDirty(state);
    case LeadDetailsStateEventType.CurrentSalesFolderSetGeneral:
      return setSalesFolderDirty(state);
    case LeadDetailsStateEventType.CurrentSalesFolderSetMemo:
      return setSalesFolderDirty(state);
    default:
      return state;
  }
}

function navigateToInvalid(state: LeadDetailsState): LeadDetailsState {
  if (queryLeadMasterInfoHasErrors(state)) {
    return setEntityFields(state, {
      masterInfo: markFormStateAsTouched(state.masterInfo),
      selection: selectTab(state.selection, LeadDetailsTabName.DetailsAndCommunications),
    });
  } else if (state.currentSalesFolder && queryLeadCurrentSalesFolderHasErrors(state)) {
    return setEntityFields(state, {
      currentSalesFolder: setEntityField(
        state.currentSalesFolder,
        'general',
        markFormStateAsTouched(state.currentSalesFolder.general)),
      selection: selectTab(state.selection, LeadDetailsTabName.SalesFolders),
    });
  } else {
    return state;
  }
}

function leadDetailsSelection(state: LeadDetailsSelection, event: LeadDetailsStateEvent): LeadDetailsSelection {
  switch (event.type) {
    case LeadDetailsStateEventType.TabSelected:
      return setEntityField(state, 'tab', event.tab);
    default:
      return state;
  }
}

function loadDetails(state: LeadDetailsState, details: LeadDetailsInit, formMode: FormMode): LeadDetailsState {
  const newState: LeadDetailsState = {
    ...state,
    formMode,
    masterInfo: formStateSnapshot({
      customer: details.customer,
      salesPerson: details.salesPerson,
      customerType: details.customerType,
    }),
    email: details.email,
    salesFolders: sfTable.create(arrayIterator(details.salesFolders)),
    currentSalesFolderInEditing: false,
    isUnsubscribed: details.isUnsubscribed,
    communications: details.communications,
    isDirty: false,
    gDriveUrl: details.gDriveUrl,
    gDriveFolderId: details.gDriveFolderId,
    version: details.version,
    activeSalesFolderId: details.activeSalesFolderId,
    settings: {
      firstContactLetterPromptPrint: details.promptPrint,
      isDppJobAllowed: details.isDppJobAllowed,
    },
  };

  return formMode === FormMode.CreateMode ? addAndChooseNewSalesFolder(newState) : newState;
}

function commitChanges(state: LeadDetailsState, saveResponse: LeadDetailsSaveResponse): LeadDetailsState {
  const { currentSalesFolder } = saveResponse;

  const initState = loadDetails(state, saveResponse, state.formMode);
  const stateWithLoadedSf = currentSalesFolder ? loadSalesFolder(initState, currentSalesFolder) : initState;
  const stateWithChoosedSf = currentSalesFolder ? chooseSalesFolder(
    stateWithLoadedSf,
    // tslint:disable-next-line:no-non-null-assertion
    getSalesFolderById(stateWithLoadedSf.salesFolders, currentSalesFolder.salesFolderId)!) : stateWithLoadedSf;

  return setPristine(stateWithChoosedSf);
}

function addAndChooseNewSalesFolder(state: LeadDetailsState): LeadDetailsState {
  const newSf = newLeadSalesFolder();
  return chooseSalesFolder(
    setEntityFields(state, {
      salesFolders: sfTable.insert(state.salesFolders, newSf),
      currentSalesFolderInEditing: true,
    }),
    newSf);
}

function loadSalesFolder(state: LeadDetailsState, salesFolder: LeadSalesFolderComplete): LeadDetailsState {
  return setEntityField(
    state,
    'salesFolders',
    sfTable.mergeById(
      state.salesFolders,
      salesFolder.salesFolderId,
      (sf) => mergeSalesFolder(sf, salesFolder)));
}

function chooseSalesFolder(state: LeadDetailsState, sf: LeadSalesFolder): LeadDetailsState {
  return setEntityField(state, 'currentSalesFolder', <CurrentSalesFolder>{
    id: getLeadSalesFolderId(sf),
    dirtyType: sf.dirtyType || DirtyType.Updated,
    general: formStateSnapshot(sf),
    jobDetails: sf.jobDetails,
    memo: sf.memo,
    version: sf.version,
    isDirty: false,
  });
}

function getFirstSalesFolder(salesFolders: Table<LeadSalesFolder>): Maybe<LeadSalesFolder> {
  return first(sfTable.byOrder(salesFolders, 'date'));
}

function getSalesFolderById(salesFolders: Table<LeadSalesFolder>, id: ID): Maybe<LeadSalesFolder> {
  return sfTable.byId(salesFolders, id);
}

function selectTab(selection: LeadDetailsSelection, tab: LeadDetailsTabName): LeadDetailsSelection {
  return setEntityField(selection, 'tab', tab);
}
